import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import { SelectButton } from "primereact/selectbutton";
import { InputSwitch } from "primereact/inputswitch";
import { Tag } from "primereact/tag";
import type { LifetimeUnit, ReportingOptions } from "../../../types/stationPerformance";
import { useLifetimePerformance } from "../hooks/useLifetimePerformance";
import MeasureValueCell from "./MeasureValueCell";
import FreeShareCell from "./FreeShareCell";
import { COCHARGE_REVENUE_TEXTS } from "../StationPerformance.consts";
import DefinitionContextBanner from "./DefinitionContextBanner";
import NeverComputedNotice from "./NeverComputedNotice";
import ExportCsvButton from "./ExportCsvButton";
import { cell, measureCell, measureReasonCell, textCell } from "../utils/csv";

interface LifetimeLeagueTableProps {
  asOf: string;
  options: ReportingOptions;
  onSelectUnit: (unitId: string) => void;
}

const euro = (cents: number) =>
  (cents / 100).toLocaleString(undefined, { style: "currency", currency: "EUR" });

/** Absolutes always favour the oldest station; the rate basis is the tenure-adjusted view. */
const BASIS_OPTIONS = [
  { label: "Absolute totals", value: "lifetime.moneyCents" },
  { label: "Per active month", value: "perActiveMonth.moneyCents" },
];

/**
 * The lifetime league table (US3).
 *
 * Both ranks sit on every row on purpose. An absolute lifetime total always favours whichever station has
 * been live longest, so a table ranked only that way bakes young-station dilution into the ranking and
 * hides the very effect CP-388 was investigating. Showing the tenure-adjusted rank beside it makes the
 * flip visible without a second query: a two-month station at 30 sessions/month outranks a two-year one
 * at 20, and that is the useful fact.
 */
const LifetimeLeagueTable: React.FC<LifetimeLeagueTableProps> = ({ asOf, options, onSelectUnit }) => {
  const [sort, setSort] = useState("lifetime.moneyCents");
  const [includeNeverActive, setIncludeNeverActive] = useState(false);
  const { report, initialLoading, neverComputed, error, fetchReport } = useLifetimePerformance({
    asOf,
    options,
    sort,
    includeNeverActive,
  });

  if (initialLoading) {
    return (
      <div className="flex justify-content-center p-5">
        <ProgressSpinner />
      </div>
    );
  }
  if (neverComputed) return <NeverComputedNotice months={neverComputed} onComputed={fetchReport} />;
  if (error) return <Message severity="error" text={error} />;
  if (!report) return <Message severity="info" text="No lifetime history yet." />;

  const rankedByRate = sort.startsWith("perActiveMonth");

  return (
    <div>
      <div className="flex flex-wrap gap-4 align-items-end mb-3">
        <div className="flex flex-column gap-1">
          <label
            className="text-sm text-color-secondary"
            title="Absolute totals favour the longest-lived station. Per active month is the tenure-adjusted view, where a young high-output station surfaces."
          >
            Rank by
          </label>
          <SelectButton
            value={sort}
            options={BASIS_OPTIONS}
            onChange={(e) => e.value && setSort(e.value)}
            allowEmpty={false}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label
            className="text-sm text-color-secondary"
            title="Units that have never charged anything. Excluded by default, since counting them would understate the typical working unit."
          >
            Show never-active
          </label>
          <InputSwitch
            checked={includeNeverActive}
            onChange={(e) => setIncludeNeverActive(!!e.value)}
          />
        </div>
        <span className="text-color-secondary text-sm">
          {report.totalItems} unit(s), lifetime to {report.asOf}
        </span>
        <ExportCsvButton
          filename={`station-performance-lifetime-${report.asOf}.csv`}
          title={`Station performance — lifetime to ${report.asOf}`}
          context={report.context}
          headers={[
            "Unit", "Station", "Connector", "Active connectors",
            "First session", "Calendar tenure months", "Months with activity", "Idle months",
            "Never active",
            "Lifetime sessions", "Lifetime energy kWh", "Lifetime money cents",
            `Lifetime ${COCHARGE_REVENUE_TEXTS.shareLabel} cents`,
            "Sessions per active month", "Sessions per active month n/a reason",
            "Money cents per active month", "Money per active month n/a reason",
            "Lifetime free share of energy %", "Lifetime free share of sessions %",
            "Rank absolute", "Rank per active month",
          ].map(cell)}
          rows={report.units.map((u) => [
            textCell(u.unitId), textCell(u.stationName), textCell(u.evseId ?? ""), cell(u.activeConnectors),
            cell(u.firstSessionDate ?? ""), cell(u.calendarTenureMonths),
            cell(u.monthsWithActivity), cell(u.gapMonths), cell(u.neverActive ? "yes" : "no"),
            cell(u.lifetime.sessions), cell(u.lifetime.energyKwh), cell(u.lifetime.moneyCents),
            cell(u.lifetime.cochargeMoneyCents),
            measureCell(u.perActiveMonth.sessions), measureReasonCell(u.perActiveMonth.sessions),
            measureCell(u.perActiveMonth.moneyCents), measureReasonCell(u.perActiveMonth.moneyCents),
            measureCell(u.lifetime.freeShareOfEnergyPercent),
            measureCell(u.lifetime.freeShareOfSessionsPercent),
            cell(u.rankAbsolute), cell(u.rankPerActiveMonth),
          ])}
        />
      </div>

      <DefinitionContextBanner context={report.context} />

      <DataTable
        value={report.units}
        dataKey="unitId"
        paginator
        rows={25}
        rowsPerPageOptions={[10, 25, 50, 100]}
        selectionMode="single"
        onSelectionChange={(e) => onSelectUnit((e.value as LifetimeUnit)?.unitId)}
        emptyMessage="No units to rank."
        size="small"
        scrollable
      >
        <Column
          header="#"
          style={{ width: "5rem" }}
          frozen
          body={(u: LifetimeUnit) => (
            <span title={rankedByRate ? "Rank per active month" : "Rank on absolute total"}>
              {rankedByRate ? u.rankPerActiveMonth : u.rankAbsolute}
            </span>
          )}
        />
        <Column field="stationName" header="Station" sortable frozen style={{ minWidth: "12rem" }} />
        <Column
          header="Other rank"
          style={{ minWidth: "8rem" }}
          body={(u: LifetimeUnit) => {
            const other = rankedByRate ? u.rankAbsolute : u.rankPerActiveMonth;
            const current = rankedByRate ? u.rankPerActiveMonth : u.rankAbsolute;
            const moved = other - current;
            return (
              <span
                className="text-color-secondary"
                title={
                  rankedByRate
                    ? `#${other} on absolute lifetime total`
                    : `#${other} per active month`
                }
              >
                #{other}
                {moved !== 0 && (
                  <span className={moved > 0 ? "text-green-600 ml-1" : "text-red-600 ml-1"}>
                    ({moved > 0 ? "+" : ""}
                    {moved})
                  </span>
                )}
              </span>
            );
          }}
        />
        <Column
          header="Tenure"
          style={{ minWidth: "10rem" }}
          body={(u: LifetimeUnit) => (
            <div className="flex flex-column">
              <span>
                {u.monthsWithActivity} active month{u.monthsWithActivity === 1 ? "" : "s"}
              </span>
              <span className="text-color-secondary text-xs">
                {u.firstSessionDate ? `since ${u.firstSessionDate}` : "never charged"}
                {u.gapMonths > 0 && (
                  <span
                    title="Calendar months where the unit charged nothing. Kept visible rather than averaged away."
                  >
                    {" "}· {u.gapMonths} idle
                  </span>
                )}
              </span>
            </div>
          )}
        />
        <Column
          header="Lifetime sessions"
          style={{ minWidth: "9rem" }}
          body={(u: LifetimeUnit) => u.lifetime.sessions.toLocaleString()}
        />
        <Column
          header="Lifetime energy"
          style={{ minWidth: "9rem" }}
          body={(u: LifetimeUnit) =>
            `${u.lifetime.energyKwh.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh`
          }
        />
        <Column
          header={options.moneyView === "COCHARGE_SHARE" ? `Lifetime ${COCHARGE_REVENUE_TEXTS.shareLabel}` : "Lifetime money"}
          style={{ minWidth: "9rem" }}
          body={(u: LifetimeUnit) => euro(u.lifetime.moneyCents)}
        />
        <Column
          header="Per active month"
          style={{ minWidth: "10rem" }}
          body={(u: LifetimeUnit) => (
            <div className="flex flex-column">
              <span>
                <MeasureValueCell measure={u.perActiveMonth.moneyCents} format="money" />
              </span>
              <span className="text-color-secondary text-xs">
                <MeasureValueCell measure={u.perActiveMonth.sessions} decimals={1} /> sessions
              </span>
            </div>
          )}
        />
        <Column
          header="Free share"
          style={{ minWidth: "9rem" }}
          body={(u: LifetimeUnit) => (
            <FreeShareCell
              energyShare={u.lifetime.freeShareOfEnergyPercent}
              sessionShare={u.lifetime.freeShareOfSessionsPercent}
            />
          )}
        />
        <Column
          header=""
          style={{ minWidth: "7rem" }}
          body={(u: LifetimeUnit) =>
            u.neverActive ? (
              <Tag
                value="never active"
                severity="warning"
                title="This unit has never charged anything, so it has no rate to rank and is excluded from averages"
              />
            ) : null
          }
        />
      </DataTable>
    </div>
  );
};

export default LifetimeLeagueTable;
